/**
 * /automations — Automation policy workspace. Surface Layer (/01)
 *
 * Configures triggers and approval policies that route through the command
 * contract. Saving an automation enqueues an `automations.configure`
 * ActionFrame; running one enqueues `automations.trigger`. The policy
 * framework is Beta: configured automations live in local view state until
 * the backend policy ledger is exposed by a runtime hook.
 */

import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Button,
  Column,
  DataTable,
  Dropdown,
  Form,
  FormGroup,
  Grid,
  InlineNotification,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
  TextInput,
  Tile,
  Toggle,
} from '@carbon/react';
import { Flow, Play } from '@carbon/icons-react';
import { useDispatchStore } from '../rebuilt/action-dispatch';
import '../styles/views.scss';

export const Route = createFileRoute('/automations')({
  component: AutomationsView,
});

const TRIGGER_OPTIONS = [
  'On document generated',
  'On low balance',
  'On approval',
  'On report generated',
  'On schedule',
] as const;

const ACTION_OPTIONS = [
  'documents.generate',
  'reports.generate',
  'knowledge.ingest',
  'queries.run',
  'automations.trigger',
] as const;

type Automation = {
  id: string;
  name: string;
  trigger: string;
  action: string;
  requires_approval: boolean;
};

const TABLE_HEADERS = [
  { key: 'name', header: 'Name' },
  { key: 'trigger', header: 'Trigger' },
  { key: 'action', header: 'Action intent' },
  { key: 'approval', header: 'Approval' },
  { key: 'run', header: 'Run' },
];

function AutomationsView() {
  const dispatch = useDispatchStore((s) => s.dispatch);
  const [automations, setAutomations] = useState<Automation[]>([]);
  const [name, setName] = useState('');
  const [trigger, setTrigger] = useState<string>(TRIGGER_OPTIONS[0]);
  const [action, setAction] = useState<string>(ACTION_OPTIONS[0]);
  const [requiresApproval, setRequiresApproval] = useState(true);
  const [result, setResult] = useState<{ status: string; detail: string } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function save() {
    const safeName = name.trim() || 'Untitled automation';
    // The automation policy ledger has no dedicated write intent yet, so the
    // policy is persisted to local view state (Beta) and the configuration is
    // carried on the real `automations.trigger` route as a `configure` op.
    setBusyId('save');
    const entry = await dispatch(
      'automations.trigger',
      { op: 'configure', name: safeName, trigger, action, requires_approval: requiresApproval },
      { label: 'Save automation', riskLevel: 'high' }
    );
    setBusyId(null);
    setResult({ status: entry.status, detail: entry.detail });
    if (entry.status === 'error' || entry.status === 'denied') return;
    setAutomations((current) => [
      ...current,
      {
        id: `automation-${current.length + 1}-${safeName}`,
        name: safeName,
        trigger,
        action,
        requires_approval: requiresApproval,
      },
    ]);
    setName('');
  }

  async function run(id: string) {
    setBusyId(id);
    const entry = await dispatch('automations.trigger', { id }, { label: 'Run automation' });
    setBusyId(null);
    setResult({ status: entry.status, detail: entry.detail });
  }

  return (
    <div className="view">
      <header className="view__header">
        <h1 className="view__title">Automations</h1>
        <p className="view__subtitle">
          Configure triggers and approval policies. Every automation runs through the same
          approval-aware command contract.
        </p>
      </header>

      <div className="surface-grid">
        <Grid condensed>
          <Column sm={4} md={8} lg={16}>
            <InlineNotification
              kind="info"
              lowContrast
              hideCloseButton
              title="Automation policy framework is Beta"
              subtitle="Triggers dispatch through the command contract today; the policy ledger is part of the beta roadmap."
            />
          </Column>

          {TRIGGER_OPTIONS.map((option) => (
            <Column key={option} sm={4} md={4} lg={4}>
              <Tile className="view-tile">
                <div className="view-tile__row">
                  <strong>{option}</strong>
                  <Flow size={20} aria-hidden="true" />
                </div>
                <p className="view-tile__label">
                  Available trigger source for an approval-aware automation policy.
                </p>
              </Tile>
            </Column>
          ))}

          <Column sm={4} md={8} lg={16}>
            <Tile className="view-tile">
              <h2 className="view-tile__value">Configure automation</h2>
              <Form
                onSubmit={(event) => {
                  event.preventDefault();
                  void save();
                }}
              >
                <FormGroup legendText="Policy">
                  <TextInput
                    id="automation-name"
                    labelText="Name"
                    placeholder="e.g. Auto-summarise new documents"
                    value={name}
                    onChange={(event) => setName(event.currentTarget.value)}
                  />
                  <Dropdown
                    id="automation-trigger"
                    titleText="Trigger type"
                    label="Trigger"
                    items={[...TRIGGER_OPTIONS]}
                    selectedItem={trigger}
                    itemToString={(item: string | null) => item ?? ''}
                    onChange={({ selectedItem }: { selectedItem: string | null }) =>
                      selectedItem && setTrigger(selectedItem)
                    }
                  />
                  <Dropdown
                    id="automation-action"
                    titleText="Action intent"
                    label="Action intent"
                    items={[...ACTION_OPTIONS]}
                    selectedItem={action}
                    itemToString={(item: string | null) => item ?? ''}
                    onChange={({ selectedItem }: { selectedItem: string | null }) =>
                      selectedItem && setAction(selectedItem)
                    }
                  />
                  <Toggle
                    id="automation-requires-approval"
                    labelText="Requires approval"
                    labelA="Off"
                    labelB="On"
                    toggled={requiresApproval}
                    onToggle={(value: boolean) => setRequiresApproval(value)}
                  />
                </FormGroup>
                <div className="view-actions">
                  <Button type="submit" renderIcon={Flow} disabled={busyId === 'save'}>
                    Save automation
                  </Button>
                </div>
              </Form>
              {result ? (
                <InlineNotification
                  kind={
                    result.status === 'error' || result.status === 'denied'
                      ? 'error'
                      : result.status === 'approval_pending'
                        ? 'info'
                        : 'success'
                  }
                  lowContrast
                  hideCloseButton
                  title={
                    result.status === 'error' || result.status === 'denied'
                      ? 'Dispatch failed'
                      : result.status === 'approval_pending'
                        ? 'Approval pending'
                        : 'Dispatched'
                  }
                  subtitle={result.detail}
                />
              ) : null}
            </Tile>
          </Column>

          <Column sm={4} md={8} lg={16}>
            {automations.length === 0 ? (
              <Tile className="view-empty">
                <h2>No automations configured</h2>
                <p className="view-tile__label">
                  Configure an automation above to populate the approval-policy ledger.
                </p>
              </Tile>
            ) : (
              <div className="surface-table-scroll">
                <DataTable rows={automations} headers={TABLE_HEADERS}>
                  {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                    <TableContainer
                      title="Approval policy"
                      description="Configured automations and their approval requirement."
                    >
                      <Table {...getTableProps()}>
                        <TableHead>
                          <TableRow>
                            {headers.map((header) => {
                              const { key, ...headerProps } = getHeaderProps({ header });
                              return (
                                <TableHeader key={key} {...headerProps}>
                                  {header.header}
                                </TableHeader>
                              );
                            })}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows.map((row) => {
                            const automation = automations.find((item) => item.id === row.id);
                            const { key, ...rowProps } = getRowProps({ row });
                            return (
                              <TableRow key={key} {...rowProps}>
                                <TableCell>{automation?.name}</TableCell>
                                <TableCell>{automation?.trigger}</TableCell>
                                <TableCell>
                                  <Tag type="cool-gray">{automation?.action}</Tag>
                                </TableCell>
                                <TableCell>
                                  {automation?.requires_approval ? (
                                    <Tag type="purple">Requires approval</Tag>
                                  ) : (
                                    <Tag type="teal">Auto-run</Tag>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Button
                                    kind="ghost"
                                    size="sm"
                                    renderIcon={Play}
                                    disabled={busyId === row.id}
                                    onClick={() => void run(row.id)}
                                  >
                                    Run now
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </DataTable>
              </div>
            )}
          </Column>
        </Grid>
      </div>
    </div>
  );
}
